import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import config from '../config';

interface ProtectedRouteProps {
  children: React.ReactNode;
  allowedStatuses: string[];
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children, allowedStatuses }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [userStatus, setUserStatus] = useState<string | null>(null);
  const location = useLocation();

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const token = localStorage.getItem('accessToken');
        if (!token) {
          setUserStatus(null);
          setIsLoading(false);
          return;
        }

        const response = await fetch(`${config.apiUrl}/auth/me`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (!response.ok) {
          throw new Error('Failed to fetch user status');
        }

        const userData = await response.json();
        const currentStatus = userData.status.toLowerCase();
        console.log('Current status:', currentStatus);

        if (currentStatus === 'active' && window.location.pathname === '/subscription') {
          setUserStatus('redirect_to_chat');
          setIsLoading(false);
          return;
        }

        if (['lite', 'verified', 'pending', 'inactive'].includes(currentStatus) && window.location.pathname === '/') {
          setUserStatus('redirect_to_subscription');
          setIsLoading(false);
          return;
        }

        const isAllowed = allowedStatuses.some(
          status => status.toLowerCase() === currentStatus
        );

        setUserStatus(isAllowed ? currentStatus : null);
      } catch (error) {
        console.error('Auth check error:', error);
        localStorage.removeItem('accessToken');
        setUserStatus(null);
      } finally {
        setIsLoading(false);
      }
    };

    checkAuth();
  }, [allowedStatuses]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white dark:bg-[#171717]">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (userStatus === 'redirect_to_chat') {
    return <Navigate to="/" replace />;
  }

  if (userStatus === 'redirect_to_subscription') {
    return <Navigate to="/subscription" replace />;
  }

  if (!userStatus) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return <>{children}</>;
};

export default ProtectedRoute; 